import React, { useState, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { Plus, Trash, Truck, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  AssetTypeSupplierMapping,
} from 'shared/types/asset';
import {
  useSupplierMappingsForType,
  useCreateSupplierMapping,
  useUpdateSupplierMapping,
  useDeleteSupplierMapping,
  useBulkApplySupplierMapping,
  useAffectedScheduleCount,
} from 'hooks/useAssets';
import { useSegments } from 'hooks/useSegments';
import { BulkUpdateSupplierDialog } from './BulkUpdateSupplierDialog';

interface CompanyOption {
  id: number;
  name: string;
}

interface SupplierMappingSectionProps {
  assetTypeId: number;
}

export const SupplierMappingSection: React.FC<SupplierMappingSectionProps> = ({
  assetTypeId,
}) => {
  const { mappings, isLoading, refetch } = useSupplierMappingsForType(assetTypeId);
  const { createMapping } = useCreateSupplierMapping();
  const { updateMapping } = useUpdateSupplierMapping();
  const { deleteMapping } = useDeleteSupplierMapping();
  const { bulkApply, isApplying } = useBulkApplySupplierMapping();
  const { fetchCount } = useAffectedScheduleCount();
  const { segments } = useSegments();

  // Bulk update dialog state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDialogMappingId, setBulkDialogMappingId] = useState<number | null>(null);
  const [bulkDialogCount, setBulkDialogCount] = useState(0);
  const [bulkDialogSupplierName, setBulkDialogSupplierName] = useState('');

  const handleAddMapping = async (supplierId: number) => {
    try {
      await createMapping(assetTypeId, { supplierId });
      await refetch();
      toast.success('Leverancier toewijzing toegevoegd');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij toevoegen';
      toast.error(msg);
    }
  };

  const handleUpdateMapping = async (
    mapping: AssetTypeSupplierMapping,
    updates: { supplierId?: number; segmentId?: number | null; conditions?: null },
  ) => {
    try {
      await updateMapping(mapping.id, updates);
      await refetch();

      // If supplier changed, check for affected schedules
      if (updates.supplierId && updates.supplierId !== mapping.supplierId) {
        try {
          const { count } = await fetchCount(mapping.id);
          if (count > 0) {
            const supplierName = mapping.supplier?.name || `Leverancier #${updates.supplierId}`;
            setBulkDialogMappingId(mapping.id);
            setBulkDialogCount(count);
            setBulkDialogSupplierName(supplierName);
            setBulkDialogOpen(true);
          }
        } catch (countErr) {
          console.error('Failed to fetch affected count:', countErr);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij opslaan';
      toast.error(msg);
    }
  };

  const handleDeleteMapping = async (mappingId: number) => {
    try {
      await deleteMapping(mappingId);
      await refetch();
      toast.success('Leverancier toewijzing verwijderd');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij verwijderen';
      toast.error(msg);
    }
  };

  const handleBulkApply = async () => {
    if (!bulkDialogMappingId) return;
    try {
      const { updated } = await bulkApply(bulkDialogMappingId);
      toast.success(`${updated} onderhoudsschema's bijgewerkt`);
      setBulkDialogOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij bijwerken';
      toast.error(msg);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Truck className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Leverancier per segment</h4>
          <Badge variant="secondary" className="text-xs">
            {mappings.length}
          </Badge>
        </div>
        <AddSupplierButton onSelect={handleAddMapping} />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laden...</p>}

      {!isLoading && mappings.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Geen leverancier toewijzingen. Wijs een standaard leverancier toe per segment
          om automatisch leveranciers in te stellen bij nieuwe onderhoudsschema&apos;s.
        </p>
      )}

      <div className="space-y-2">
        {mappings.map((mapping) => (
          <MappingCard
            key={mapping.id}
            mapping={mapping}
            segments={segments}
            onUpdate={handleUpdateMapping}
            onDelete={handleDeleteMapping}
          />
        ))}
      </div>

      <BulkUpdateSupplierDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        affectedCount={bulkDialogCount}
        supplierName={bulkDialogSupplierName}
        onConfirm={handleBulkApply}
        onSkip={() => setBulkDialogOpen(false)}
        isApplying={isApplying}
      />
    </div>
  );
};

// ─── Add Supplier Button with inline search ──────────────────────────

interface AddSupplierButtonProps {
  onSelect: (supplierId: number) => void;
}

const AddSupplierButton: React.FC<AddSupplierButtonProps> = ({ onSelect }) => {
  const { session } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CompanyOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (searchTerm.length >= 2 && session) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const res = await fetchWithAuth(
            `${API_URL}/contact/search-companies?query=${encodeURIComponent(searchTerm)}`,
            { method: 'GET' },
            session,
          );
          if (res.ok) {
            const data = await res.json();
            setResults(data.map((c: any) => ({ id: c.id, name: c.name })));
          }
        } catch (err) {
          console.error('Company search failed:', err);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, session]);

  const handleSelect = (companyId: number) => {
    onSelect(companyId);
    setIsOpen(false);
    setSearchTerm('');
    setResults([]);
  };

  if (!isOpen) {
    return (
      <Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
        <Plus className="h-3 w-3 mr-1" />
        Leverancier toewijzing
      </Button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1">
        <div className="relative">
          <MagnifyingGlass className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Zoek leverancier..."
            className="h-8 text-sm pl-7 w-56"
            autoFocus
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {(results.length > 0 || isSearching) && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64 max-h-48 overflow-auto rounded-md border bg-popover shadow-md">
          {isSearching && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Zoeken...
            </div>
          )}
          {results.map((company) => (
            <div
              key={company.id}
              onClick={() => handleSelect(company.id)}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-accent"
            >
              {company.name}
            </div>
          ))}
          {!isSearching && searchTerm.length >= 2 && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Geen resultaten
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Individual Mapping Card ─────────────────────────────────────────

interface SegmentOption {
  id: number;
  name: string;
}

interface MappingCardProps {
  mapping: AssetTypeSupplierMapping;
  segments: SegmentOption[];
  onUpdate: (mapping: AssetTypeSupplierMapping, updates: any) => void;
  onDelete: (id: number) => void;
}

const MappingCard: React.FC<MappingCardProps> = ({
  mapping,
  segments,
  onUpdate,
  onDelete,
}) => {
  const segmentOptions = [
    { value: '', label: 'Geen (standaard leverancier)' },
    ...segments.map((s) => ({ value: String(s.id), label: s.name })),
  ];

  const handleSegmentChange = (val: string) => {
    onUpdate(mapping, {
      segmentId: val ? Number(val) : null,
      conditions: null,
    });
  };

  return (
    <Card className="border">
      <CardContent className="p-3 space-y-3">
        {/* Row 1: Supplier name + delete */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-1">
            <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium">
              {mapping.supplier?.name || `Leverancier #${mapping.supplierId}`}
            </span>
            {!mapping.segmentId && (
              <Badge variant="outline" className="text-xs">
                Standaard
              </Badge>
            )}
          </div>
          <ChangeSupplierButton
            currentSupplierId={mapping.supplierId}
            onSelect={(supplierId) => onUpdate(mapping, { supplierId })}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={() => onDelete(mapping.id)}
          >
            <Trash className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Row 2: Segment selection */}
        <div>
          <Label className="text-xs mb-1.5 block text-muted-foreground">
            Segment (leeg = standaard leverancier)
          </Label>
          <SimpleDropdown
            value={mapping.segmentId ? String(mapping.segmentId) : ''}
            options={segmentOptions}
            onChange={handleSegmentChange}
            placeholder="Geen (standaard leverancier)"
            triggerClassName="h-8 text-sm"
          />
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Change Supplier Button (inline search) ──────────────────────────

interface ChangeSupplierButtonProps {
  currentSupplierId: number;
  onSelect: (supplierId: number) => void;
}

const ChangeSupplierButton: React.FC<ChangeSupplierButtonProps> = ({
  currentSupplierId,
  onSelect,
}) => {
  const { session } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CompanyOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (searchTerm.length >= 2 && session) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const res = await fetchWithAuth(
            `${API_URL}/contact/search-companies?query=${encodeURIComponent(searchTerm)}`,
            { method: 'GET' },
            session,
          );
          if (res.ok) {
            const data = await res.json();
            setResults(
              data
                .map((c: any) => ({ id: c.id, name: c.name }))
                .filter((c: CompanyOption) => c.id !== currentSupplierId),
            );
          }
        } catch (err) {
          console.error('Company search failed:', err);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, session, currentSupplierId]);

  const handleSelect = (companyId: number) => {
    onSelect(companyId);
    setIsOpen(false);
    setSearchTerm('');
    setResults([]);
  };

  if (!isOpen) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs"
        onClick={() => setIsOpen(true)}
      >
        Wijzig
      </Button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1">
        <div className="relative">
          <MagnifyingGlass className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Zoek..."
            className="h-7 text-xs pl-6 w-40"
            autoFocus
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
          }}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      {(results.length > 0 || isSearching) && (
        <div className="absolute right-0 top-full mt-1 z-50 w-56 max-h-48 overflow-auto rounded-md border bg-popover shadow-md">
          {isSearching && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Zoeken...
            </div>
          )}
          {results.map((company) => (
            <div
              key={company.id}
              onClick={() => handleSelect(company.id)}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-accent"
            >
              {company.name}
            </div>
          ))}
          {!isSearching && searchTerm.length >= 2 && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Geen resultaten
            </div>
          )}
        </div>
      )}
    </div>
  );
};
